import { inject, Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiResponse, ApiResponseStatusCode, IntegrationRepository, Plan, PlanRepository, SendCase } from '../core';
import { PlanSendForm } from '../models';


/**
 * This service handles plan sending.
 */
@Injectable({ providedIn: 'root' })
export class SendService {

	private readonly planRepo = inject(PlanRepository);
	private readonly integrationRepo = inject(IntegrationRepository);

	/**
	* Get plan by plan guid
	* @param {string} planGuid The plan guid
	*/
	getPlan(planGuid: string): Observable<Plan> {
		return this.planRepo.getPlan(planGuid).pipe(
			map(res => this.handlePlanResponse(res))
		);
	}

	/**
	* Send case to a recipient.
	* @param {string} id Plan ID.
	* @param {SendCase} sendPlan Case data to send.
	* @returns Operation success.
	*/
	private sendPlan(id: string, sendPlan: SendCase): Observable<void> {
		return this.integrationRepo.sendCase(id, sendPlan).pipe(
			map(res => this.handleSendResponse(res))
		);
	}

	/**
	* Send case to a recipient by form.
	* @param {PlanSendForm} sendForm Form of plan to send.
	* @returns Operation success.
	*/
	sendForm(sendForm: PlanSendForm): Observable<void> {
		if (sendForm) {
			const planToSend: SendCase = {
				userId: sendForm.userGuid,
				recipient: sendForm.userName,
				notes: sendForm.notes
			};
			return this.sendPlan(sendForm.planGuid, planToSend);
		}
		return throwError(() => new Error('Send Form is null'));
	}


	private handlePlanResponse(response: ApiResponse<Plan>) {
		switch (response.statusCode) {
			case ApiResponseStatusCode.Success: return response.result as Plan;
			case ApiResponseStatusCode.CaseNumberNotUnique: throw new Error('PlanNumberNotUnique');
			default: throw new Error('Generic error');
		}
	}

	private handleSendResponse(response: ApiResponse<void>) {
		switch (response.statusCode) {
			case ApiResponseStatusCode.Success: return response.result;
			case ApiResponseStatusCode.UserNotFound: throw new Error('UserNotFound');
			case ApiResponseStatusCode.UserNotJpsProd: throw new Error('UserNotJpsProd');
			case ApiResponseStatusCode.UserNotFitboneProd: throw new Error('UserNotFitboneProd');
			case ApiResponseStatusCode.UserNotTlhexProd: throw new Error('UserNotTlhexProd');
			case ApiResponseStatusCode.CaseVersionMismatch: throw new Error('CaseVersionMismatch');
			default: throw new Error('Generic error');
		}
	}

}
